import React, { useMemo } from 'react';
import Badge from '../../../components/ui/Badge';
import { LockIcon } from '../../../components/ui/Icons';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { ReferenceDataGeneralList, type ReferenceInboxColumn } from '../../parameters/ReferenceDataGeneralList';
import { moduleConfig } from './config';
import {
  isLockedSheetColumn,
  sheetColumnLockReason,
  useMgaSheetColumns,
  type MgaSheetColumn,
} from './useData';

const BEHAVIOR_LABEL: Record<string, string> = {
  MANUAL: 'Manual',
  CARRY_FORWARD: 'Carry forward',
  DERIVED: 'Derived',
  REQUEST_MIRROR: 'Request mirror',
};

const List: React.FC = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const formatDate = moduleConfig.utils.formatDate;

  const columns = useMemo<ReferenceInboxColumn<MgaSheetColumn>[]>(
    () => [
      {
        key: 'code',
        header: 'Code',
        className: 'whitespace-nowrap font-mono text-xs',
        render: (row) => (
          <span className="font-medium tracking-wide">{row.code}</span>
        ),
      },
      {
        key: 'name',
        header: 'Name',
        render: (row) => {
          const locked = isLockedSheetColumn(row);
          const reason = sheetColumnLockReason(row);
          return (
            <span className="inline-flex items-center gap-1.5">
              <span>{row.name}</span>
              {locked ? (
                <span
                  className={cn(
                    'inline-flex items-center',
                    isDark ? 'text-amber-400' : 'text-amber-600',
                  )}
                  title={reason ?? undefined}
                >
                  <LockIcon className="h-3.5 w-3.5" />
                </span>
              ) : null}
            </span>
          );
        },
      },
      {
        key: 'behavior',
        header: 'Behavior',
        className: 'hidden sm:table-cell',
        render: (row) => BEHAVIOR_LABEL[row.behavior] ?? row.behavior,
      },
      {
        key: 'columnType',
        header: 'Type',
        className: 'hidden md:table-cell',
        render: (row) => row.columnType,
      },
      {
        key: 'showInGrid',
        header: 'Grid',
        className: 'hidden lg:table-cell',
        render: (row) => (
          <Badge variant={row.showInGrid ? 'success' : 'secondary'}>
            {row.showInGrid ? 'Yes' : 'No'}
          </Badge>
        ),
      },
      {
        key: 'isActive',
        header: 'Active',
        render: (row) => (
          <Badge variant={row.isActive ? 'success' : 'danger'}>{row.isActive ? 'Yes' : 'No'}</Badge>
        ),
      },
      {
        key: 'sortOrder',
        header: 'Order',
        className: 'hidden xl:table-cell',
        render: (row) => row.sortOrder,
      },
      {
        key: 'updatedAt',
        header: 'Updated',
        className: 'hidden xl:table-cell',
        render: (row) => formatDate(row.updatedAt),
      },
    ],
    [formatDate, isDark],
  );

  return (
    <ReferenceDataGeneralList
      moduleConfig={moduleConfig}
      useData={useMgaSheetColumns}
      columns={columns}
      defaultSort={{ key: 'sortOrder', direction: 'asc' }}
      newLabel="New sheet column"
      getRowLabel={(row) => row.name}
      canDeleteRow={(row) => !isLockedSheetColumn(row)}
    />
  );
};

export default List;
